import { supabase } from './supabase';

export interface SearchResult {
  id: string;
  full_name: string;
  title: string | null;
  summary: string | null;
  location: string | null;
  privacy_level: string;
  avatar_url: string | null;
  current_company: string | null;
  skills: string[] | null;
  experience: any | null;
  education: any | null;
  match_score?: number;
  saved_to_projects?: string[];
}

export interface SearchFilters {
  location?: string;
  skills?: string[];
  experienceLevel?: string;
  educationLevel?: string;
}

export async function searchProfiles(
  query: string | null,
  filters: SearchFilters = {},
  hasRecruiterLicense: boolean = false,
  hasProLicense: boolean = false,
  userId?: string
): Promise<SearchResult[]> {
  try {
    // Fetch profiles with related data
    const { data: searchData, error: searchError } = await supabase
      .from('profiles')
      .select(`
        id,
        full_name,
        title,
        summary,
        location,
        privacy_level,
        avatar_url,
        experiences (
          id,
          position,
          company_name,
          current
        ),
        education (
          id,
          institution,
          degree,
          field_of_study
        )
      `)
      .order('created_at', { ascending: false });

    if (searchError) throw searchError;

    // Filter results based on search query and filters
    const filteredResults = (searchData || []).filter(profile => {
      // Search query matching
      if (query) {
        const searchTerms = query.toLowerCase().split(' ');
        const searchableText = [
          profile.full_name,
          profile.title,
          profile.summary,
          profile.location,
          ...profile.experiences?.map(e => `${e.position} ${e.company_name}`),
          ...profile.education?.map(e => `${e.degree} ${e.institution} ${e.field_of_study}`)
        ].filter(Boolean).join(' ').toLowerCase();

        // Match all search terms
        if (!searchTerms.every(term => searchableText.includes(term))) {
          return false;
        }
      }

      // Location filter
      if (filters.location && !profile.location?.toLowerCase().includes(filters.location.toLowerCase())) {
        return false;
      }

      // Privacy level check
      if (!hasRecruiterLicense && profile.privacy_level === 'private') {
        return false;
      }

      return true;
    });

    return filteredResults.map(profile => ({
      ...profile,
      current_company: profile.experiences?.find(e => e.current)?.company_name || null,
      saved_to_projects: []
    }));
  } catch (error) {
    console.error('Error searching profiles:', error);
    return [];
  }
}

export async function getRecruiterProjects(recruiterId: string) {
  try {
    const { data, error } = await supabase
      .from('recruiting_projects')
      .select('id, title')
      .eq('profile_id', recruiterId)
      .eq('is_active', true)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching recruiter projects:', error);
    return [];
  }
}

// Temporary placeholder functions for save/unsave functionality
export async function saveCandidate(candidateId: string, projectId: string, userId: string): Promise<boolean> {
  try {
    console.log('Saving candidate', candidateId, 'to project', projectId);
    return true;
  } catch (error) {
    console.error('Error saving candidate:', error);
    return false;
  }
}

export async function unsaveCandidate(candidateId: string, projectId: string, userId: string): Promise<boolean> {
  try {
    console.log('Unsaving candidate', candidateId, 'from project', projectId);
    return true;
  } catch (error) {
    console.error('Error unsaving candidate:', error);
    return false;
  }
}